import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { StatusDot } from '../../components/ui';
import { getInitials } from '../modes';
import {
  formatCommentTime,
  normalizeCommentText,
  type CommentAnchor,
  type CommentStreamEntry,
} from './commentsModel';

/**
 * CommentsPanel (board 7a): 360px overlay on the right of the Workspace with a
 * left shadow. It never changes the Mode.
 *
 * Header with item name and close · list of comments and system events in time
 * order (avatar, author, time, Anchor chip, text) · composer with
 * "Anchored to: …", textarea and Post.
 *
 * Requirements: 12.1, 12.2, 12.3. Light theme only.
 */
export interface CommentsPanelProps {
  itemName: string;
  entries: readonly CommentStreamEntry[];
  /** Where a new comment will attach: the selection, or the current Mode. */
  anchor: CommentAnchor;
  onPost: (text: string) => Promise<void>;
  onClose: () => void;
  loadError?: boolean;
  /**
   * Called when the user selects an Anchor chip. When omitted, chips are plain
   * labels (Requirement 12.4).
   */
  onSelectAnchor?: (anchor: CommentAnchor) => void;
  /** Short message shown when an Anchor could not be opened, for example a locked Mode. */
  notice?: string | null;
  /** Reference time for relative timestamps. */
  now?: Date;
}

const CHIP_CLASS =
  'inline-flex max-w-full items-center truncate rounded-full bg-[#EDE7FF] px-2 py-0.5 text-[11px] font-bold text-[#5A21C9]';

const AnchorChip: React.FC<{
  anchor: CommentAnchor;
  onSelect?: (anchor: CommentAnchor) => void;
}> = ({ anchor, onSelect }) =>
  onSelect ? (
    <button
      type="button"
      data-testid="anchor-chip"
      data-anchor-type={anchor.type}
      data-anchor-id={anchor.id}
      title="Go to this in the workspace"
      aria-label={`Go to ${anchor.label}`}
      onClick={() => onSelect(anchor)}
      className={`${CHIP_CLASS} hover:bg-[#DDD2FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]`}
    >
      {anchor.label}
    </button>
  ) : (
    <span
      data-testid="anchor-chip"
      data-anchor-type={anchor.type}
      data-anchor-id={anchor.id}
      className={CHIP_CLASS}
    >
      {anchor.label}
    </span>
  );

export const CommentsPanel: React.FC<CommentsPanelProps> = ({
  itemName,
  entries,
  anchor,
  onPost,
  onClose,
  loadError = false,
  onSelectAnchor,
  notice,
  now,
}) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listEndRef = useRef<HTMLDivElement>(null);

  // Keep the newest entry in view.
  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [entries.length]);

  const normalized = normalizeCommentText(text);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!normalized || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onPost(normalized);
      setText('');
    } catch {
      setError('The comment could not be posted. Your text is kept. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside
      aria-label="Comments"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      className="absolute inset-y-0 right-0 z-20 flex w-[360px] flex-col border-l border-gray-200 bg-white shadow-[-8px_0_24px_rgba(28,20,40,0.08)]"
    >
      <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-[18px] py-3.5">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">Comments</p>
          <h2 className="truncate text-base font-bold text-gray-900" title={itemName}>
            {itemName}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close comments"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
        >
          <X size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </header>

      {notice ? (
        <p
          role="status"
          data-testid="anchor-notice"
          className="border-b border-[#fef08a] bg-[#fefce8] px-[18px] py-2 text-xs text-[#854d0e]"
        >
          {notice}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5">
        {loadError ? (
          <p role="alert" className="rounded-lg bg-[#fee2e2] px-3 py-2 text-sm text-[#991b1b]">
            Comments could not be loaded. Close the panel and open it again to retry.
          </p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-600">
            No comments yet. Write the first one below; it attaches to what you have selected.
          </p>
        ) : (
          <ol className="flex flex-col gap-4" aria-label="Comment stream">
            {entries.map((entry) =>
              entry.kind === 'comment' ? (
                <li key={entry.id} data-testid="comment" className="flex gap-2.5">
                  <span
                    aria-hidden="true"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#834DFB] text-xs font-bold text-white"
                  >
                    {getInitials(entry.author)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                      <span className="font-bold text-gray-900">{entry.author}</span>
                      <span className="text-xs text-gray-500">
                        {formatCommentTime(entry.createdAt, now)}
                      </span>
                    </p>
                    <div className="mt-1">
                      <AnchorChip anchor={entry.anchor} onSelect={onSelectAnchor} />
                    </div>
                    <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-gray-700">
                      {entry.text}
                    </p>
                  </div>
                </li>
              ) : (
                <li
                  key={entry.id}
                  data-testid="system-event"
                  className="flex items-center gap-2 text-xs text-gray-600"
                >
                  <StatusDot tone="idle" />
                  <span className="min-w-0 break-words">{entry.text}</span>
                  {entry.anchor ? (
                    <AnchorChip anchor={entry.anchor} onSelect={onSelectAnchor} />
                  ) : null}
                  <span className="shrink-0 text-gray-500">
                    {formatCommentTime(entry.createdAt, now)}
                  </span>
                </li>
              )
            )}
          </ol>
        )}
        <div ref={listEndRef} />
      </div>

      <form onSubmit={submit} className="border-t border-gray-100 bg-white px-[18px] py-3.5">
        <p className="mb-1.5 text-xs text-gray-600">
          Anchored to: <span className="font-bold text-[#5A21C9]">{anchor.label}</span>
        </p>
        <label htmlFor="comment-text" className="sr-only">
          Comment
        </label>
        <textarea
          id="comment-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
          rows={3}
          placeholder="Write a comment"
          className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-60"
        />
        {error ? (
          <p role="alert" className="mt-1.5 text-xs text-[#991b1b]">
            {error}
          </p>
        ) : null}
        <div className="mt-2 flex justify-end">
          <button
            type="submit"
            disabled={!normalized || busy}
            aria-busy={busy}
            title={!normalized ? 'Write a comment to post it' : undefined}
            className="inline-flex h-9 min-w-[72px] items-center justify-center rounded-lg bg-[#834DFB] px-4 text-sm font-bold text-white hover:bg-[#6E2EF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Posting…' : 'Post'}
          </button>
        </div>
      </form>
    </aside>
  );
};

export default CommentsPanel;
